<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const route = useRoute();

const copy = {
  empty: 'Nenhuma proposta vinculada a este contato.',
  openPipeline: 'Abrir esteira',
  titleFallback: 'Proposta',
  updatedAt: 'Atualizada em',
  amount: 'Valor',
  cards: {
    total: 'Total',
    inProgress: 'Andamento',
    paid: 'Pagas',
    canceled: 'Canceladas',
  },
  statuses: {
    em_andamento: 'Em andamento',
    aguardando_assinatura: 'Aguardando assinatura',
    pago: 'Paga',
    cancelado: 'Cancelada',
    recusado: 'Recusada',
  },
  stages: {
    simulacao: 'Simulação',
    assinatura: 'Assinatura',
    pagamento: 'Pagamento',
    concluido: 'Concluído',
  },
};

const proposalsSummary = computed(
  () => props.selectedContact?.talkvia_proposals || {}
);

const latestProposals = computed(() => {
  const latest = proposalsSummary.value.latest;
  return Array.isArray(latest) ? latest : [];
});

const summaryCards = computed(() => [
  {
    key: 'total',
    label: copy.cards.total,
    value: proposalsSummary.value.total || 0,
  },
  {
    key: 'in_progress',
    label: copy.cards.inProgress,
    value: proposalsSummary.value.in_progress || 0,
  },
  {
    key: 'paid',
    label: copy.cards.paid,
    value: proposalsSummary.value.paid || 0,
  },
  {
    key: 'canceled',
    label: copy.cards.canceled,
    value: proposalsSummary.value.canceled || 0,
  },
]);

const proposalsUrl = computed(
  () => `/app/accounts/${route.params.accountId}/proposals`
);

const hasProposals = computed(() => latestProposals.value.length > 0);

const formatMoney = value => {
  const numberValue = Number(value || 0);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numberValue);
};

const formatDate = value => {
  if (!value) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const statusLabel = value => copy.statuses[value] || value || '-';
const stageLabel = value => copy.stages[value] || value || '-';
</script>

<template>
  <div class="flex flex-col gap-5 px-6 py-6">
    <div class="grid grid-cols-2 gap-3">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
      >
        <p class="text-xs font-medium uppercase text-n-slate-11">
          {{ card.label }}
        </p>
        <p class="mt-1 text-2xl font-semibold text-n-slate-12">
          {{ card.value }}
        </p>
      </div>
    </div>

    <div v-if="hasProposals" class="flex flex-col gap-3">
      <div
        v-for="proposal in latestProposals"
        :key="proposal.id"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h4 class="truncate text-sm font-semibold text-n-slate-12">
              {{ proposal.title || copy.titleFallback }}
            </h4>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ stageLabel(proposal.stage) }}
            </p>
          </div>
          <span
            class="shrink-0 rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-12"
          >
            {{ statusLabel(proposal.status) }}
          </span>
        </div>

        <div class="mt-4 grid grid-cols-2 gap-3 text-xs text-n-slate-11">
          <div>
            <p>{{ copy.amount }}</p>
            <p class="mt-1 font-medium text-n-slate-12">
              {{ formatMoney(proposal.amount) }}
            </p>
          </div>
          <div>
            <p>{{ copy.updatedAt }}</p>
            <p class="mt-1 font-medium text-n-slate-12">
              {{ formatDate(proposal.updated_at) }}
            </p>
          </div>
        </div>
      </div>

      <a
        :href="proposalsUrl"
        class="text-sm font-medium text-n-blue-text hover:underline"
      >
        {{ copy.openPipeline }}
      </a>
    </div>

    <p v-else class="py-10 text-center text-sm leading-6 text-n-slate-11">
      {{ copy.empty }}
    </p>
  </div>
</template>
